import { StyleSheet } from 'aphrodite';

import {
  backgroundGrey,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  mismatchAmber,
  mismatchRed,
  modernSalmon,
  modernSalmonBackground,
  overrideOlive,
  positiveGreen,
  shadowLight,
  softAmber,
  softGreenDark,
  softPurple,
  softPurpleLight,
  white,
} from 'lib/css/colors';

// Shared aphrodite styles for the Brand SKU Mapper MFR->DP review view and its sub-components.
export const styles = StyleSheet.create({
  container: { minHeight: '100vh', backgroundColor: backgroundGrey },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
    borderLeft: `4px solid ${grape}`,
    paddingLeft: 12,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  headerMeta: { color: coolGrey, fontSize: 12 },
  runWarning: { color: modernSalmon, fontSize: 12, fontWeight: 600, marginLeft: 8 },
  title: { color: blackberry, fontWeight: 700, fontSize: 18 },
  runningIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 12,
    color: softAmber,
    fontWeight: 600,
    gap: 4,
  },
  progressTrack: {
    width: 140,
    height: 6,
    borderRadius: 3,
    backgroundColor: greyStroke,
    overflow: 'hidden',
  },
  progressFill: { height: '100%', backgroundColor: grape, transition: 'width 0.4s ease' },
  dispatchBtn: {
    backgroundColor: grape,
    borderColor: grape,
    color: white,
    fontWeight: 600,
    fontSize: 12,
    padding: '4px 12px',
  },
  newRunBtn: { fontSize: 12, padding: '4px 12px', fontWeight: 600 },
  limitInput: { width: 80, height: 30, fontSize: 12, padding: '4px 8px' },
  eligibleCountText: { color: coolGrey, fontSize: 11, whiteSpace: 'nowrap' },
  totalBadge: {
    backgroundColor: softPurpleLight,
    color: blackberry,
    fontSize: 13,
    padding: '6px 10px',
  },
  reviewWrapper: { maxWidth: 1400, margin: '0 auto' },
  navRow: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  navBtn: {
    minWidth: 130,
    backgroundColor: blackberry,
    borderColor: blackberry,
    color: white,
    fontWeight: 600,
  },
  positionText: {
    color: coolGrey,
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  jumpInput: {
    width: 64,
    height: 28,
    fontSize: 13,
    fontWeight: 600,
    padding: '2px 6px',
    textAlign: 'center',
  },
  statusBanner: {
    marginTop: 12,
    padding: '8px 14px',
    borderRadius: 8,
    backgroundColor: softPurpleLight,
    color: softPurple,
    fontWeight: 700,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontSize: 12,
  },
  productCard: {
    position: 'relative',
    borderRadius: 12,
    backgroundColor: white,
    border: `1px solid ${greyStroke}`,
    padding: 16,
    paddingRight: 46,
    height: '100%',
  },
  sideLabel: {
    display: 'flex',
    alignItems: 'center',
    height: 30,
    color: coolGrey,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontWeight: 700,
    marginBottom: 14,
  },
  productImageWrap: { width: 220, height: 220, margin: '0 auto 14px' },
  productImageLarge: {
    width: 220,
    height: 220,
    objectFit: 'contain',
    borderRadius: 10,
    border: 'none',
    backgroundColor: white,
  },
  productImagePlaceholderLarge: {
    width: 220,
    height: 220,
    borderRadius: 10,
    backgroundColor: softPurpleLight,
  },
  productName: { color: blackberry, fontWeight: 600, fontSize: 17, marginBottom: 4 },
  mutedText: { color: coolGrey, fontSize: 15 },
  mismatchBadge: {
    backgroundColor: mismatchAmber,
    color: 'white',
    borderRadius: 8,
    padding: '2px 6px',
    fontSize: 11,
    fontWeight: 600,
  },
  mismatchBadgeNoSignal: { backgroundColor: mismatchRed },
  overrideReason: {
    color: overrideOlive,
    fontSize: 12,
    fontStyle: 'italic',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  overrideReasonNoSignal: { color: mismatchRed, fontWeight: 600 },
  candBottomBlock: {
    marginTop: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  candHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 30,
    marginBottom: 14,
  },
  candHeaderText: { color: blackberry, fontWeight: 600, fontSize: 13 },
  // Padding so the 2px selection ring isn't clipped by the scroll edge.
  candScrollArea: { maxHeight: 640, overflowY: 'auto', padding: 6 },
  candGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  candCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    // Let the grid item shrink to its 1fr track instead of growing to the nowrap
    // min-content width of its lines (which would overflow the grid and clip cards).
    minWidth: 0,
    border: `1px solid ${greyStroke}`,
    borderRadius: 12,
    padding: 12,
    paddingRight: 42,
    backgroundColor: white,
    cursor: 'pointer',
    ':hover': { borderColor: grape, boxShadow: `0 2px 8px ${shadowLight}` },
  },
  candCardSelected: {
    borderColor: grape,
    backgroundColor: softPurpleLight,
    boxShadow: `0 0 0 2px ${softPurpleLight}`,
  },
  candCardMatched: { borderColor: positiveGreen, boxShadow: `0 0 0 2px ${positiveGreen}` },
  approvedByLine: {
    color: softGreenDark,
    fontSize: 12,
    fontWeight: 600,
    marginTop: 4,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rankBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    color: white,
    backgroundColor: blackberry,
    fontWeight: 700,
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 8,
    zIndex: 1,
  },
  candTopRightRow: {
    position: 'absolute',
    top: 10,
    right: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    zIndex: 1,
  },
  candStatusPill: {
    flexBasis: '100%',
    textAlign: 'right',
    color: coolGrey,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.2,
  },
  tabRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  tabRowRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  exportBtn: { whiteSpace: 'nowrap' },
  dataSourceLabel: {
    display: 'inline-block',
    color: coolGrey,
    backgroundColor: backgroundGrey,
    border: `1px solid ${greyStroke}`,
    fontSize: 9,
    fontWeight: 600,
    padding: '3px 7px',
    borderRadius: 8,
    whiteSpace: 'nowrap',
    maxWidth: 110,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tierPillFloating: {
    color: white,
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 8,
  },
  candImageWrap: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    marginTop: 18,
    marginBottom: 10,
  },
  candImage: {
    width: 96,
    height: 96,
    objectFit: 'contain',
    borderRadius: 8,
    border: 'none',
    backgroundColor: white,
  },
  candImagePlaceholder: {
    width: 96,
    height: 96,
    borderRadius: 8,
    backgroundColor: softPurpleLight,
  },
  candName: {
    color: blackberry,
    fontWeight: 600,
    fontSize: 15,
    marginBottom: 2,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  candIconRail: {
    position: 'absolute',
    top: '50%',
    right: 8,
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    zIndex: 1,
  },
  productIconRail: { top: 154 },
  candIconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: white,
    boxShadow: `0 1px 4px ${shadowLight}`,
    color: coolGrey,
    fontSize: 15,
    cursor: 'pointer',
    ':hover': { color: grape, backgroundColor: softPurpleLight },
  },
  candIconButtonDisabled: {
    color: greyStroke,
    cursor: 'not-allowed',
    ':hover': { color: greyStroke, backgroundColor: 'transparent' },
  },
  zoomModalDialog: { width: 'fit-content', maxWidth: '95vw' },
  zoomModalBody: {
    position: 'relative',
    display: 'flex',
    gap: 32,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  zoomCloseButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: white,
    boxShadow: `0 1px 4px ${shadowLight}`,
    color: coolGrey,
    fontSize: 18,
    cursor: 'pointer',
    zIndex: 2,
    ':hover': { color: blackberry },
  },
  zoomDetails: { display: 'flex', flexDirection: 'column', gap: 4, width: 320, maxWidth: '100%' },
  zoomName: {
    display: 'block',
    WebkitLineClamp: 'unset',
    overflow: 'visible',
    marginBottom: 4,
    fontSize: 18,
    fontWeight: 700,
  },
  zoomTierPill: { alignSelf: 'flex-start', marginBottom: 4, fontSize: 15 },
  zoomReasoningRow: { marginTop: 6 },
  actionsRow: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: backgroundGrey,
    marginTop: 12,
    paddingTop: 12,
    paddingBottom: 12,
    borderBottom: `1px solid ${greyStroke}`,
  },
  approveBtn: {
    backgroundColor: positiveGreen,
    borderColor: positiveGreen,
    color: white,
    fontWeight: 600,
    ':hover': { backgroundColor: softGreenDark, borderColor: softGreenDark },
  },
  neutralBtn: {
    fontWeight: 600,
    backgroundColor: white,
    borderColor: grape,
    color: blackberry,
    ':hover': { backgroundColor: softPurpleLight },
  },
  unapproveBtn: {
    position: 'absolute',
    top: 6,
    left: 6,
    zIndex: 3,
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    backgroundColor: white,
    borderColor: grape,
    color: blackberry,
    ':hover': { backgroundColor: softPurpleLight },
  },
  rejectBtn: {
    backgroundColor: modernSalmonBackground,
    borderColor: modernSalmonBackground,
    color: modernSalmon,
    fontWeight: 600,
    ':hover': { backgroundColor: modernSalmon, color: white },
  },
  hintText: {
    color: coolGrey,
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
});
